import { useState } from 'react';
import { Tab, TabList, TabPanel } from '@simplywallst/ui-core';

import { Slider } from '../../Slider';
import { tooltipContent } from '../tooltipContent';
import { getCompanyFinancialData } from '../../../utils/calculation';
import type { NarrativeValuatorCompany } from '../../../hooks/narrativeValuatorCompanyFragment';
import { INPUT_VALUE_RANGES } from '../../../utils/validation';
import { LabelledValue } from './LabelledValue';
import { VALUATOR_RATE_PRECISION } from '@/pages/Narrative/utils/schema';
import type { NarrativeValuatorForm } from '../utils';

type TabPanelProps = 'revenue' | 'earnings' | 'priceEarnings';

export type Props = {
  onChange: (data: NarrativeValuatorForm) => void;
  values: NarrativeValuatorForm;
  company: NarrativeValuatorCompany;
};

export const NarrativeValuatorInput = ({
  onChange,
  values,
  company,
}: Props) => {
  const [activeTab, setActiveTab] = useState<TabPanelProps>('revenue');
  const { priceEarningsMultiple, profitMarginPercentage, revenuePercentage } =
    values;
  const companyFinancialData = getCompanyFinancialData(company);

  const handleOnChange = (type: string) => {
    return (value: number) => {
      onChange({
        ...values,
        [type]: value,
      });
    };
  };

  return (
    <div className="grid h-fit grid-rows-[max-content_max-content] rounded-b-x1 bg-p-grey-50">
      <TabList className="grid grid-cols-[repeat(3,auto)] gap-x1 p-x2">
        <Tab
          styleType="period"
          hasPanel
          selected={activeTab === 'revenue'}
          key="revenue"
          value="revenue"
          onClick={() => setActiveTab('revenue')}
        >
          <label
            className="m-inherit cursor-pointer text-xs"
            htmlFor="revenue-input"
          >
            Revenue
          </label>
        </Tab>
        <Tab
          styleType="period"
          hasPanel
          selected={activeTab === 'earnings'}
          key="earnings"
          value="earnings"
          onClick={() => setActiveTab('earnings')}
          title="Profit Margin"
          id="earnings-tab"
        >
          <label
            className="m-inherit cursor-pointer text-xs"
            htmlFor="earnings-input"
          >
            Profit Margin
          </label>
        </Tab>
        <Tab
          styleType="period"
          hasPanel
          selected={activeTab === 'priceEarnings'}
          key="priceEarnings"
          value="priceEarnings"
          onClick={() => setActiveTab('priceEarnings')}
          id="price-earnings-tab"
        >
          <label
            className="m-inherit cursor-pointer text-xs"
            htmlFor="price-earnings-input"
          >
            Future PE
          </label>
        </Tab>
      </TabList>
      <TabPanel
        selected={activeTab === 'revenue'}
        value="revenue"
        className="p-x2"
      >
        <div className="mb-x4">
          <Slider
            inputId="revenue-input"
            identifier="narrative-valuator-revenue-percentage"
            minRange={INPUT_VALUE_RANGES.revenuePercentage.min}
            maxRange={INPUT_VALUE_RANGES.revenuePercentage.max}
            value={revenuePercentage}
            precision={VALUATOR_RATE_PRECISION}
            onChange={handleOnChange('revenuePercentage')}
            suffix="% p.a."
            bands={[
              {
                threshold: 0,
                trackColor: 'var(--s-color-bad)',
                label: 'Decrease',
                align: 'left',
              },
              {
                threshold: 20,
                trackColor: 'var(--s-color-warning)',
              },
              {
                threshold: 300,
                trackColor: 'var(--s-color-good)',
                label: 'Increase',
                align: 'right',
              },
            ]}
          />
        </div>
        <div className="grid gap-x2">
          <LabelledValue label="Current revenue growth rate">
            {companyFinancialData.revenuePercentage.toFixed(2)}%
          </LabelledValue>

          <LabelledValue
            label={`${companyFinancialData.secondaryIndustryFriendlyName} revenue
            growth rate`}
          >
            {typeof companyFinancialData.industryRevenueGrowth === 'number'
              ? `${companyFinancialData.industryRevenueGrowth.toFixed(2)}%`
              : 'N/A'}
          </LabelledValue>
        </div>
      </TabPanel>
      <TabPanel
        selected={activeTab === 'earnings'}
        value="earnings"
        className="p-x2"
      >
        <div className="mb-x4">
          <Slider
            inputId="earnings-input"
            identifier="narrative-valuator-profit-margin-percentage"
            minRange={INPUT_VALUE_RANGES.profitMarginPercentage.min}
            maxRange={INPUT_VALUE_RANGES.profitMarginPercentage.max}
            value={profitMarginPercentage}
            precision={VALUATOR_RATE_PRECISION}
            onChange={handleOnChange('profitMarginPercentage')}
            suffix="%"
            bands={[
              {
                threshold: INPUT_VALUE_RANGES.profitMarginPercentage.min,
                label: 'Decrease',
                align: 'left',
              },
              {
                threshold: INPUT_VALUE_RANGES.profitMarginPercentage.max,
                label: 'Increase',
                align: 'right',
              },
            ]}
          />
        </div>
        <div className="grid gap-x2">
          <LabelledValue
            label="Current"
            labelTerm="Net Profit Margin"
            tooltip={tooltipContent.profitMarginPercentage}
          >
            {companyFinancialData.profitMarginPercentage.toFixed(2)}%
          </LabelledValue>

          <LabelledValue
            label={companyFinancialData.secondaryIndustryFriendlyName}
            labelTerm="Net Profit Margin"
            tooltip={tooltipContent.industryNetProfitMargin}
          >
            {typeof companyFinancialData.industryNetProfitMarginPercentage ===
            'number'
              ? `${companyFinancialData.industryNetProfitMarginPercentage.toFixed(
                  2
                )}%`
              : 'N/A'}
          </LabelledValue>
        </div>
      </TabPanel>
      <TabPanel
        selected={activeTab === 'priceEarnings'}
        value="priceEarnings"
        className="p-x2"
      >
        <div className="mb-x4">
          <Slider
            inputId="price-earnings-input"
            identifier="narrative-valuator-price-earnings-multiple"
            minRange={INPUT_VALUE_RANGES.priceEarningsMultiple.min}
            maxRange={INPUT_VALUE_RANGES.priceEarningsMultiple.recommendedMax}
            maxManual={Number.MAX_SAFE_INTEGER}
            value={priceEarningsMultiple}
            precision={VALUATOR_RATE_PRECISION}
            onChange={handleOnChange('priceEarningsMultiple')}
            suffix="x"
            bands={[
              {
                threshold: INPUT_VALUE_RANGES.priceEarningsMultiple.min,
                label: 'Decrease',
                align: 'left',
              },
              {
                threshold:
                  INPUT_VALUE_RANGES.priceEarningsMultiple.recommendedMax,
                label: 'Increase',
                align: 'right',
              },
            ]}
          />
        </div>
        <div className="grid gap-x2">
          <LabelledValue
            label="Current"
            labelTerm="Price to Earnings ratio"
            tooltip={tooltipContent.priceEarningsMultiple}
          >
            {companyFinancialData.priceEarningsMultiple?.toFixed(2) ?? ''}x
          </LabelledValue>
          <LabelledValue
            label="Forward"
            labelTerm="Price to Earnings ratio"
            tooltip={tooltipContent.forwardPriceEarningsMultiple}
          >
            {companyFinancialData.forwardPriceEarningsMultiple1Y?.toFixed(2) ??
              ''}
            x
          </LabelledValue>
          <LabelledValue
            label={companyFinancialData.secondaryIndustryFriendlyName}
            labelTerm="Price to Earnings ratio"
            tooltip={tooltipContent.industryPriceEarningsMultiple}
          >
            {typeof companyFinancialData.industryPriceEarningMultiple ===
            'number'
              ? `${companyFinancialData.industryPriceEarningMultiple.toFixed(
                  2
                )}x`
              : 'N/A'}
          </LabelledValue>
        </div>
      </TabPanel>
    </div>
  );
};
